// learnerEvents — the learning signals OUTSIDE an item's work session (#570).
//
// workLog records how one item was built. This records what happens between
// items: signing in, pages visited, a lesson opened and finished, an inline
// item seen, an assessment or story opened and left, a tour's lifecycle, code
// compiled or run, an upgrade or "Notify me" clicked, a payment window closed.
// The server accepts a fixed list of kinds (LEARNER_EVENT_KINDS in
// backend/server.py) and refuses anything else per event.
//
// Same rules as workLog: learning events only (no tab focus, no keystrokes, no
// free text), and it never breaks the app — every failure is a silent no-op.

import { authFetch } from '@/services/projectService';

const FLUSH_MS = 5000;
const FLUSH_AT = 20;
const MAX_BATCH = 50;     // the server's cap
const MAX_BUFFER = 300;   // a runaway loop must not eat memory

// undefined = auth not resolved yet (events wait), null = signed out (no-op),
// a string = the learner the buffered events belong to.
let learner;
let buffer = [];
let timer = null;
let flushing = false;

const url = () => `${process.env.REACT_APP_BACKEND_URL || ''}/api/events`;

// AuthContext tells us who is signed in. A different learner, or nobody, means
// the buffer is not theirs to send.
export function setLearner(id) {
  const next = id || null;
  if (learner !== undefined && learner !== next) buffer = [];
  learner = next;
  if (learner && buffer.length) flushEvents();
}

// Record one signal. Fire-and-forget: nothing awaits this.
export function track(kind, ref = null, payload = {}) {
  if (!kind || learner === null) return;
  if (buffer.length >= MAX_BUFFER) return;
  buffer.push({ kind, ref: ref == null ? null : String(ref), payload: payload || {}, client_at: new Date().toISOString() });
  if (buffer.length >= FLUSH_AT) {
    flushEvents();
  } else if (!timer) {
    timer = setTimeout(flushEvents, FLUSH_MS);
  }
}

export async function flushEvents({ keepalive = false } = {}) {
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
  if (flushing || !learner || buffer.length === 0) return;
  flushing = true;
  const batch = buffer.slice(0, MAX_BATCH);
  buffer = buffer.slice(MAX_BATCH);
  try {
    const res = await authFetch(url(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ events: batch }),
      keepalive,
    });
    // A 4xx is a verdict on the batch (signed out, too big); retrying cannot
    // change it and would wedge the buffer. Drop it. A 5xx is worth retrying.
    if (!res.ok && res.status >= 500) requeue(batch);
  } catch {
    requeue(batch);   // offline: keep it for the next flush
  } finally {
    flushing = false;
  }
  if (buffer.length && !timer) timer = setTimeout(flushEvents, FLUSH_MS);
}

function requeue(batch) {
  buffer = [...batch, ...buffer].slice(0, MAX_BUFFER);
}

// The tab going away is the last reliable moment to send; keepalive lets the
// request outlive the page.
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushEvents({ keepalive: true });
  });
}

// Test seam only.
export function __resetLearnerEvents() {
  learner = undefined;
  buffer = [];
  if (timer) clearTimeout(timer);
  timer = null;
  flushing = false;
}

export function __bufferedEvents() {
  return buffer;
}
